import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Eye,
  Library,
  List as ListIcon,
  Loader2,
  Play,
  SlidersHorizontal,
  X,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  METRIC_TOOLTIP_TITLE_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { accountSettingsFieldDescriptionClassName } from "../Profile/accountSettingsCardShell";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import type { DateRange } from "@/types/trade";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { fillViewportHeightPx } from "@/utils/strategyRdFillViewport";
import { strategyRdMetaClassName } from "./strategyRdCardShell";

/**
 * Height that fills the viewport below the element's layout top. Returns a callback ref: the
 * element may mount later than the hook (loading states), so it measures when it attaches, and
 * again on resize. Page scroll must not change the height — otherwise TES / chart / dock grow.
 */
export function useFillViewportHeight(bottomPadPx = 16): [(el: HTMLDivElement | null) => void, number] {
  const elRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(640);
  const measure = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const next = fillViewportHeightPx(el, bottomPadPx);
    setHeight((prev) => (Math.abs(prev - next) > 1 ? next : prev));
  }, [bottomPadPx]);
  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      if (el) measure();
    },
    [measure]
  );
  useLayoutEffect(() => {
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        measure();
      });
    };
    measure();
    window.addEventListener("resize", schedule);
    const ro = new ResizeObserver(schedule);
    const parent = elRef.current?.parentElement;
    if (parent) ro.observe(parent);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
    };
  }, [measure]);
  return [setRef, height];
}

type Option = { value: string; label: string };

/** Window presets in days; the timeframe's cap decides which show. */
const STRATEGY_RD_WINDOW_PRESETS: [number, string][] = [
  [30, "1M"], [90, "3M"], [180, "6M"], [365, "1Y"], [730, "2Y"], [1826, "5Y"], [3652, "10Y"], [7305, "20Y"],
];

export function strategyRdWindowLabel(days: number, long = false): string {
  if (days >= 365) {
    const y = Math.round(days / 365.25);
    return long ? `${y} year${y === 1 ? "" : "s"}` : `${y}Y`;
  }
  if (days >= 28) {
    const m = Math.round(days / 30);
    return long ? `${m} month${m === 1 ? "" : "s"}` : `${m}M`;
  }
  return long ? `${days} day${days === 1 ? "" : "s"}` : `${days}D`;
}

/**
 * The chart's bottom bar, left side: how much history to test (TradingView-style range
 * buttons), capped per timeframe, with a spinner while the chart's history loads.
 */
export interface StrategyRdDateRange {
  /** YYYY-MM-DD */
  start: string;
  end: string;
}

function ymdToDate(ymd: string): Date | null {
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateToYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A specific backtest period instead of "the last N days": the app's own date range picker
 * (Dashboard / Statistics), limited to the history this timeframe keeps.
 */
function StrategyRdDateRangeButton({
  value,
  onChange,
  lookbackCap,
}: {
  value: StrategyRdDateRange | null;
  onChange: (next: StrategyRdDateRange | null) => void;
  lookbackCap: number;
}): JSX.Element {
  const fromValue = (v: StrategyRdDateRange | null): DateRange => ({
    startDate: v ? ymdToDate(v.start) : null,
    endDate: v ? ymdToDate(v.end) : null,
  });
  // The picker reports the first click on its own; the period applies once both ends are set.
  const [draft, setDraft] = useState<DateRange>(() => fromValue(value));
  useEffect(() => setDraft(fromValue(value)), [value?.start, value?.end]);
  const earliest = new Date(Date.now() - lookbackCap * 86_400_000);
  earliest.setHours(0, 0, 0, 0);
  return (
    <div data-strategy-rd-date-range="">
      <DateRangePicker
        dateRange={draft}
        onDateRangeChange={(next) => {
          setDraft(next);
          if (next.startDate && next.endDate) {
            onChange({ start: dateToYmd(next.startDate), end: dateToYmd(next.endDate) });
          } else if (!next.startDate && !next.endDate) {
            onChange(null);
          }
        }}
        emptyLabel="Custom dates"
        showPresets={false}
        minDate={earliest}
      />
    </div>
  );
}

export function StrategyRdWindowPicker({
  lookbackDays,
  lookbackCap,
  onLookbackChange,
  chartFrom = null,
  candleLabel = null,
  pendingRun = false,
  dateRange = null,
  onDateRangeChange,
}: {
  /** A specific period; when set the presets are not active. */
  dateRange?: StrategyRdDateRange | null;
  onDateRangeChange?: (next: StrategyRdDateRange | null) => void;
  /** A backtest is open on another window: the new one needs a run. */
  pendingRun?: boolean;
  lookbackDays: number;
  lookbackCap: number;
  onLookbackChange: (days: number) => void;
  /** First candle drawn ("Jul 2021"). */
  chartFrom?: string | null;
  candleLabel?: string | null;
}): JSX.Element {
  const presets = STRATEGY_RD_WINDOW_PRESETS.filter(([d]) => d <= lookbackCap);
  if (!presets.some(([d]) => d === lookbackCap)) presets.push([lookbackCap, strategyRdWindowLabel(lookbackCap)]);
  const custom = !dateRange && !presets.some(([d]) => d === lookbackDays);
  const help =
    `Backtest window. Up to ${strategyRdWindowLabel(lookbackCap, true)} on ${candleLabel ?? "this timeframe"}` +
    ". Longer on higher timeframes. Backtests use the whole window" +
    (chartFrom ? `; the chart draws the latest candles (from ${chartFrom}).` : ".");
  return (
    <div className="flex shrink-0 items-center gap-2" role="group" aria-label="Backtest window" data-strategy-rd-lookback="">
      {/* The app's tabs (Statistics / Trade Log): no tab is active while custom dates are set. */}
      <Tabs
        data-trade-log-tabs=""
        value={dateRange ? "" : String(lookbackDays)}
        onValueChange={(v) => onLookbackChange(Math.min(Number(v), lookbackCap))}
        title={help}
      >
        <TabsList className="min-w-max">
          {presets.map(([days, label]) => (
            <TabsTrigger key={days} value={String(days)} className="px-2.5 tabular-nums">
              {label}
            </TabsTrigger>
          ))}
          {custom ? (
            <TabsTrigger value={String(lookbackDays)} className="px-2.5 tabular-nums">
              {strategyRdWindowLabel(lookbackDays)}
            </TabsTrigger>
          ) : null}
        </TabsList>
      </Tabs>
      {onDateRangeChange ? (
        <StrategyRdDateRangeButton value={dateRange} onChange={onDateRangeChange} lookbackCap={lookbackCap} />
      ) : null}
      {pendingRun ? (
        <span
          className="whitespace-nowrap font-[family-name:var(--font-primary)] text-xs font-medium text-[color:var(--brand-tropical-surf)]"
          data-strategy-rd-window-pending=""
        >
          Run backtest to test {strategyRdWindowLabel(lookbackDays, true)}
        </span>
      ) : null}
    </div>
  );
}

/** The chart's bottom bar, right side: times in UTC or the trader's own zone. */
export function StrategyRdTimeZoneToggle({
  mode,
  localTimeZone,
  onChange,
}: {
  mode: "local" | "utc";
  localTimeZone: string;
  onChange: (mode: "local" | "utc") => void;
}): JSX.Element {
  return (
    <Tabs
      data-trade-log-tabs=""
      value={mode}
      onValueChange={(v) => onChange(v as "local" | "utc")}
      aria-label="Times shown in"
      data-strategy-rd-time-zone=""
    >
      <TabsList className="min-w-max">
        <TabsTrigger value="local" className="px-2.5" title={`Times in ${localTimeZone}`}>
          Local
        </TabsTrigger>
        <TabsTrigger value="utc" className="px-2.5" title="Times in UTC">
          UTC
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}

/**
 * One weekly allowance as a progress ring inside a button-shaped box (the same 32px outline
 * box as the top bar's buttons): share used inside the ring, what's left in the app's
 * metric tooltip.
 */
export function StrategyRdUsageRing({
  label,
  used,
  cap,
  unit,
  period = "this week",
}: {
  label: string;
  used: number;
  cap: number;
  /** "credits", "prompts", "backtests": for the tooltip. */
  unit: string;
  /** "this week" | "this month" (the plan's allowance period). */
  period?: string;
}): JSX.Element {
  const share = cap > 0 ? Math.min(1, Math.max(0, used / cap)) : 0;
  const pct = Math.round(share * 100);
  const left = Math.max(0, cap - used);
  const r = 11;
  const c = 2 * Math.PI * r;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            // Same box as Backtest history beside it: 36px tall, grey outline and text.
            "box-border inline-flex !h-9 shrink-0 items-center gap-1.5 !rounded-[6px] !border !border-solid !border-border !bg-background !py-0 !pl-1 !pr-3",
            "font-[family-name:var(--font-primary)] text-sm font-medium normal-case leading-none tracking-normal !text-gray-500 dark:!text-white",
            "transition-colors hover:!bg-muted"
          )}
          aria-label={`${label}: ${pct}% used, ${left} of ${cap} ${unit} left ${period}`}
          data-strategy-rd-usage={unit}
        >
          <svg viewBox="0 0 28 28" className="size-6 shrink-0" aria-hidden>
            <g transform="rotate(-90 14 14)">
              <circle cx="14" cy="14" r={r} fill="none" stroke="var(--border)" strokeWidth="2.5" />
              <circle
                cx="14"
                cy="14"
                r={r}
                fill="none"
                stroke={pct >= 90 ? "var(--brand-glacial-red)" : "var(--brand-tropical-surf)"}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={`${share * c} ${c}`}
              />
            </g>
            <text
              x="14"
              y="14.5"
              textAnchor="middle"
              dominantBaseline="middle"
              fill="currentColor"
              style={{ fontFamily: "var(--font-secondary)", fontSize: pct >= 100 ? 7.5 : 9, fontWeight: 600 }}
            >
              {pct}%
            </text>
          </svg>
          <span>{label}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        className={metricTooltipShellClassName("max-w-56")}
        arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
      >
        <p className={METRIC_TOOLTIP_TITLE_CLASS}>{label} {period}</p>
        <p className={cn("mt-1 text-[11px] leading-snug", METRIC_TOOLTIP_BODY_CLASS)}>
          {left} of {cap} {unit} left · {used} used
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Lab actions in the app's top bar, beside the breadcrumb: TES usage (messages, backtests),
 * Backtest history and Run backtest. Setups and the settings open from the taskbar.
 */
export function StrategyRdLabTopBarActions({
  usage = null,
  onPreview,
  previewBusy = false,
  previewDisabled = false,
  onOpenLibrary,
  onRunBacktest,
  runDisabledReason,
  busy,
  busyLabel,
}: {
  /** This week's TES allowance (null while loading or when TES is off). */
  usage?: {
    turnsUsed: number;
    turnsCap: number;
    /** "credits" on paid plans (this month), "prompts" on Free (this week). */
    aiMode?: "credits" | "prompts" | null;
    backtestsUsed: number | null;
    backtestsCap: number | null;
    backtestsWindow?: "week" | "month" | null;
  } | null;
  /** Show this recipe's setups on the chart (no backtest). Omitted when there's nothing to preview. */
  onPreview?: () => void;
  previewBusy?: boolean;
  previewDisabled?: boolean;
  onOpenLibrary: () => void;
  onRunBacktest: () => void;
  /** Why Run backtest is unavailable (its tooltip), or null. */
  runDisabledReason: string | null;
  busy: boolean;
  busyLabel: string | null;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2" data-strategy-rd-top-actions="">
      {usage ? (
        <TooltipProvider delayDuration={150}>
        <div className="flex items-center gap-2" data-strategy-rd-usage-rings="">
          {usage.aiMode === "credits" ? (
            <StrategyRdUsageRing label="AI credits" used={usage.turnsUsed} cap={usage.turnsCap} unit="credits" period="this month" />
          ) : (
            <StrategyRdUsageRing label="Prompts" used={usage.turnsUsed} cap={usage.turnsCap} unit="prompts" period="this week" />
          )}
          {usage.backtestsCap != null && usage.backtestsUsed != null ? (
            <StrategyRdUsageRing
              label="Backtests"
              used={usage.backtestsUsed}
              cap={usage.backtestsCap}
              unit="backtests"
              period={usage.backtestsWindow === "month" ? "this month" : "this week"}
            />
          ) : null}
        </div>
        </TooltipProvider>
      ) : null}
      {onPreview ? (
        <Button
          type="button"
          variant="outline-brand"
          size="sm"
          data-font="primary"
          className="h-8 gap-1.5 px-2.5 shadow-none"
          onClick={onPreview}
          disabled={previewDisabled || previewBusy}
          data-strategy-rd-preview=""
          title="Show this recipe's setups on the chart. Quick, no backtest"
        >
          {previewBusy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <Eye className="size-3.5" aria-hidden />}
          <span className="normal-case tracking-normal">{previewBusy ? "Previewing" : "Preview"}</span>
        </Button>
      ) : null}
      <Button
        type="button"
        variant="outline-brand"
        size="sm"
        data-font="primary"
        className="h-8 gap-1.5 px-3 shadow-none"
        onClick={onOpenLibrary}
        data-strategy-rd-open-library=""
      >
        <Library className="size-3.5" aria-hidden />
        <span className="normal-case tracking-normal">Backtest history</span>
      </Button>
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className="h-8 px-3.5"
        data-strategy-rd-run-backtest=""
        onClick={onRunBacktest}
        title={runDisabledReason ?? undefined}
        disabled={busy || Boolean(runDisabledReason)}
      >
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Play className="size-3.5" aria-hidden />}
        <span className="normal-case tracking-normal">{busy ? busyLabel || "Running…" : "Run backtest"}</span>
      </Button>
    </div>
  );
}

export type StrategyRdLabPanel = "setups" | "pine" | "chart";

export type StrategyRdTaskbarItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  active?: boolean;
  disabled?: boolean;
  /** Tooltip when disabled (why). */
  hint?: string;
  onClick: () => void;
};

/** The thin bar on the far right, top to bottom: one button per tool; the open one is tinted. */
export function StrategyRdLabTaskbar({ items }: { items: StrategyRdTaskbarItem[] }): JSX.Element {
  return (
    <nav
      className="flex h-full w-12 shrink-0 flex-col items-center gap-1 !border-0 !border-solid !border-border !border-l bg-background py-2"
      aria-label="Lab tools"
      data-strategy-rd-taskbar=""
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            aria-label={item.label}
            aria-pressed={item.active}
            title={item.disabled && item.hint ? `${item.label} · ${item.hint}` : item.label}
            disabled={item.disabled}
            onClick={item.onClick}
            data-strategy-rd-taskbar-item={item.id}
            className={cn(
              "flex !size-9 items-center justify-center !rounded-[6px] transition-colors disabled:opacity-40",
              item.active
                ? "!bg-[color:var(--brand-tropical-surf)]/12 !text-[color:var(--brand-tropical-surf)]"
                : "!text-[#6B7280] hover:!bg-muted hover:!text-[color:var(--brand-tropical-surf)]"
            )}
          >
            <Icon className="size-[18px]" aria-hidden />
          </button>
        );
      })}
    </nav>
  );
}

const SPLIT_KEY = "strategyRd.lab.chartSplit";
const SPLIT_MIN = 0.35;
const SPLIT_MAX = 0.85;
/** The chart's toolbar, candles, key and window bar all fit above this; it never gets smaller. */
const CHART_MIN_PX = 380;

function readSplit(): number {
  try {
    const n = Number(window.localStorage.getItem(SPLIT_KEY));
    return Number.isFinite(n) && n >= SPLIT_MIN && n <= SPLIT_MAX ? n : 0.57;
  } catch {
    return 0.57;
  }
}

/**
 * Chart on top, always in view; backtest results below it. With results the chart takes a share
 * of the height (drag the handle; it's remembered) and only the results scroll — the page never does.
 */
export function StrategyRdChartStack({
  chart,
  results,
  showResults,
  resultsLabel,
}: {
  chart: React.ReactNode;
  /** The results dock (or its empty hint when `showResults` is false). */
  results: React.ReactNode;
  showResults: boolean;
  /** Collapsed bar's label, e.g. "Backtest results · Session ORB". */
  resultsLabel?: string;
}): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const [split, setSplit] = useState(readSplit);
  const [collapsed, setCollapsed] = useState(false);
  const splitRef = useRef(split);
  splitRef.current = split;

  const onHandleDown = (e: React.PointerEvent) => {
    const host = hostRef.current;
    if (!host) return;
    e.preventDefault();
    const rect = host.getBoundingClientRect();
    const move = (ev: PointerEvent) => {
      const next = (ev.clientY - rect.top) / rect.height;
      const floor = Math.max(SPLIT_MIN, Math.min(SPLIT_MAX, CHART_MIN_PX / Math.max(1, rect.height)));
      setSplit(Math.min(SPLIT_MAX, Math.max(floor, next)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      try {
        window.localStorage.setItem(SPLIT_KEY, String(splitRef.current));
      } catch {
        // Storage blocked — the split lasts until reload.
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const open = showResults && !collapsed;
  return (
    <div ref={hostRef} className="flex min-h-0 min-w-0 flex-1 flex-col" data-strategy-rd-chart-stack="">
      <div
        className={cn("relative flex min-h-0 flex-col", open ? "shrink-0" : "flex-1")}
        style={open ? { height: `${split * 100}%`, minHeight: CHART_MIN_PX } : undefined}
        data-strategy-rd-chart-area=""
      >
        {chart}
      </div>
      {showResults ? (
        <>
          {open ? (
            <div
              className="group relative flex h-4 shrink-0 cursor-row-resize items-center justify-center !border-0 !border-solid !border-border !border-t bg-muted/40"
              onPointerDown={onHandleDown}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize chart and results"
              data-strategy-rd-results-handle=""
            >
              <span className="h-1 w-8 rounded-full bg-border transition-colors group-hover:bg-[color:var(--brand-tropical-surf)]/60" aria-hidden />
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setCollapsed(true)}
                aria-label="Hide results"
                title="Hide results: the chart takes the full height"
                className="absolute right-2 top-1/2 flex !size-5 -translate-y-1/2 items-center justify-center !rounded-[4px] !text-[#9CA3AF] hover:!bg-muted hover:!text-[color:var(--brand-tropical-surf)]"
              >
                <ChevronDown className="size-3.5" aria-hidden />
              </button>
            </div>
          ) : null}
          {open ? (
            <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden" data-strategy-rd-results-scroll="">
              {results}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              className="flex h-9 shrink-0 items-center justify-between gap-2 !border-0 !border-t !border-solid !border-border !px-4 font-[family-name:var(--font-primary)] !text-sm !text-[#6B7280] hover:!text-[color:var(--brand-tropical-surf)]"
              data-strategy-rd-results-show=""
            >
              <span className="truncate">{resultsLabel || "Backtest results"}</span>
              <span className="flex items-center gap-1 text-[color:var(--brand-tropical-surf)]">
                Show <ChevronUp className="size-4" aria-hidden />
              </span>
            </button>
          )}
        </>
      ) : (
        <div className="shrink-0">{results}</div>
      )}
    </div>
  );
}

/** Chart settings (taskbar): an on/off row with a description. */
/** A settings row with the app's Switch (Settings / Notifications): label and hint, control right. */
export function StrategyRdSettingSwitch({
  label,
  hint,
  checked,
  onChange,
  swatch,
  ...rest
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  swatch?: React.ReactNode;
} & { [key: `data-${string}`]: string }): JSX.Element {
  return (
    <div className="flex w-full items-center gap-4 py-3" {...rest}>
      {swatch ? <span className="flex w-8 shrink-0 justify-center">{swatch}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
          {label}
        </span>
        {hint ? <span className={accountSettingsFieldDescriptionClassName}>{hint}</span> : null}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}

/** Two mini candles in a palette's up / down colours. */
type CandlePalette = { up: string; down: string; wick?: string; borderUp?: string; borderDown?: string };

function CandleSwatch({ up, down, wick, borderUp, borderDown }: CandlePalette): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <line x1="5" y1="2" x2="5" y2="15" stroke={wick ?? up} strokeWidth="1.2" />
      <rect x="2.5" y="5" width="5" height="7" rx="0.5" fill={up} stroke={borderUp ?? up} strokeWidth="0.8" />
      <line x1="13" y1="3" x2="13" y2="16" stroke={wick ?? down} strokeWidth="1.2" />
      <rect x="10.5" y="6" width="5" height="8" rx="0.5" fill={down} stroke={borderDown ?? down} strokeWidth="0.8" />
    </svg>
  );
}

/** Chart settings (taskbar): pick the candle palette. */
export function StrategyRdCandleColorPicker<T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<CandlePalette & { id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label="Candle colours" className="grid grid-cols-2 gap-1.5" data-strategy-rd-setting="candles">
      {options.map((o) => {
        const selected = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(o.id)}
            className={cn(
              "flex items-center gap-2 rounded-md !border !border-solid !px-2 !py-1.5 text-left transition-colors",
              selected
                ? "!border-[color:var(--brand-tropical-surf)] bg-[color:var(--brand-tropical-surf)]/[0.06]"
                : "!border-border hover:!border-[color:var(--brand-tropical-surf)]/60"
            )}
            data-strategy-rd-candle-scheme={o.id}
          >
            <CandleSwatch {...o} />
            <span
              className={cn(
                "min-w-0 truncate font-[family-name:var(--font-primary)] !text-[12px] leading-tight",
                selected ? "!text-[color:var(--brand-tropical-surf)]" : "!text-[#6B7280]"
              )}
            >
              {o.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Quote currency picker, now in Chart settings (it used to sit on the bar above the chart). */
export function StrategyRdQuoteSelect({
  options,
  value,
  onChange,
}: {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
}): JSX.Element | null {
  if (options.length === 0) return null;
  return (
    <FieldSelectFit id="strategy-rd-quote-variant" value={value} onValueChange={onChange} options={options} placeholder="Quote" />
  );
}

/**
 * The results under the chart, in the page's flow: the workspace column scrolls, so the chart
 * keeps its full height and reading a backtest never squeezes it. ``maxHeight`` /
 * ``defaultHeight`` are kept for callers; the section sizes to its content.
 */
export function StrategyRdResultsDock({
  header,
  children,
  emptyState,
}: {
  maxHeight?: number;
  header: React.ReactNode | null;
  children?: React.ReactNode;
  /** Shown instead of header / body before any backtest; null shows nothing. */
  emptyState?: React.ReactNode;
  defaultHeight?: number;
}): JSX.Element | null {
  if (!header) {
    // Nothing to say yet (no recipe): no strip under the chart.
    if (!emptyState) return null;
    return (
      <div className="shrink-0 !border-0 !border-solid !border-border !border-t px-4 py-3" data-strategy-rd-results-dock="empty">
        {emptyState}
      </div>
    );
  }
  return (
    // The page canvas behind white cards, as on Statistics: each section stands on its own.
    <section className="flex min-h-full shrink-0 flex-col gap-4 bg-content-canvas p-5" data-strategy-rd-results-dock="">
      {header}
      <div className="flex flex-col gap-4" data-strategy-rd-results-body="">
        {children}
      </div>
    </section>
  );
}

const DRAWER_MIN = 420;
const DRAWER_EDGE = 24;

function readDrawerWidth(key: string): number | null {
  try {
    const n = Number(window.localStorage.getItem(`strategyRd.lab.drawer.${key}`));
    return Number.isFinite(n) && n >= DRAWER_MIN ? n : null;
  } catch {
    return null;
  }
}

/**
 * Recipe / Backtest settings drawer: as wide as the content wants (up to the viewport), with
 * a draggable left edge. Inline width so the base Sheet's `max-w-sm` can never squeeze it.
 */
export function StrategyRdLabDrawer({
  open,
  onOpenChange,
  storageKey,
  defaultWidth,
  title,
  description,
  footer,
  headerExtra,
  onWidthChange,
  inline = false,
  children,
  ...dataProps
}: {
  /** Lab: a column between the chart and the taskbar (like Chart settings), not a sheet over the page. */
  inline?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storageKey: string;
  defaultWidth: number;
  title: React.ReactNode;
  description: React.ReactNode;
  footer?: React.ReactNode;
  /** Under the title (e.g. tabs). */
  headerExtra?: React.ReactNode;
  /** The drawer's current width while open, 0 when closed — so the page can make room for it. */
  onWidthChange?: (width: number) => void;
  children: React.ReactNode;
} & { [key: `data-${string}`]: string }): JSX.Element | null {
  const [viewport, setViewport] = useState(() => window.innerWidth);
  const [width, setWidth] = useState(() => readDrawerWidth(storageKey) ?? defaultWidth);
  const dragRef = useRef<{ x: number; w: number } | null>(null);
  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const maxWidth = Math.max(320, viewport - DRAWER_EDGE);
  const shown = Math.min(Math.max(width, Math.min(DRAWER_MIN, maxWidth)), maxWidth);
  useEffect(() => {
    // Inline it takes its own room in the layout — nothing to pad.
    onWidthChange?.(open && !inline ? shown : 0);
  }, [inline, onWidthChange, open, shown]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setWidth(Math.max(DRAWER_MIN, d.w + (d.x - e.clientX)));
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.removeProperty("user-select");
      setWidth((w) => {
        try {
          window.localStorage.setItem(`strategyRd.lab.drawer.${storageKey}`, String(Math.round(w)));
        } catch {
          // ignore
        }
        return w;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [storageKey]);

  const resizeHandle = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      title="Drag to resize"
      className="group absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize"
      onPointerDown={(e) => {
        e.preventDefault();
        dragRef.current = { x: e.clientX, w: shown };
        document.body.style.setProperty("user-select", "none");
      }}
      onDoubleClick={() => setWidth(defaultWidth)}
    >
      <div className="mx-auto h-full w-px bg-transparent transition-colors group-hover:bg-[color:var(--brand-tropical-surf)]" />
    </div>
  );

  if (inline) {
    if (!open) return null;
    // A share of the window at most, so the chart beside it keeps its room.
    const inlineWidth = Math.min(shown, Math.max(DRAWER_MIN, Math.round(viewport * 0.38)));
    return (
      <aside
        // Shrinks (to 320) before the chart drops under its minimum width.
        className="relative flex h-full min-w-[320px] shrink flex-col overflow-hidden !border-0 !border-l !border-solid !border-border bg-background"
        style={{ width: inlineWidth }}
        {...dataProps}
      >
        {resizeHandle}
        <div className="flex shrink-0 flex-col gap-1 !border-0 !border-b !border-solid !border-border py-3 pl-5 pr-2">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">{title}</p>
              <p className="mt-0.5 font-[family-name:var(--font-primary)] text-xs text-[#9CA3AF]">{description}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-[#6B7280]"
              aria-label="Close panel"
              onClick={() => onOpenChange(false)}
            >
              <X className="size-3.5" aria-hidden />
            </Button>
          </div>
          {headerExtra}
        </div>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 !border-0 !border-t !border-solid !border-border bg-background px-5 py-3">
            {footer}
          </div>
        ) : null}
      </aside>
    );
  }

  return (
    // Not modal: the chart beside the panel stays sharp and usable while the recipe or the
    // settings are edited (no dimmed, blurred page), and the page's scroll lock — which also
    // swallowed wheel / trackpad scrolling inside the panel's dropdown lists — is never applied.
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="gap-0 overflow-hidden border-l border-border p-0 shadow-2xl data-[side=right]:max-w-none data-[side=right]:sm:max-w-none"
        style={{ width: shown, maxWidth: "100vw" }}
        // Clicking the chart (to look at a setup) does not close the panel; Close and Esc do.
        onInteractOutside={(e) => e.preventDefault()}
        {...dataProps}
      >
        {resizeHandle}
        <SheetHeader className="shrink-0 border-b border-border px-5 py-4 pr-12">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
          {headerExtra}
        </SheetHeader>
        <div className="min-h-0 min-w-0 flex-1 overflow-auto">{children}</div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border bg-popover px-5 py-3">
            {footer}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

/** A quiet line for the dock before there's a backtest. */
export function StrategyRdDockHint({ children }: { children: React.ReactNode }): JSX.Element {
  return <p className={cn(strategyRdMetaClassName, "text-sm")}>{children}</p>;
}
